import type { PrintJobResult } from '@warehouse/shared';

import { api } from '../../../lib/api-client';

/**
 * CROSS-MODULE — printing is owned by M9 (`devices-printing`), and this port is
 * now bound to its real endpoint.
 *
 * The pallet detail page and the bin drawer both need a "print label" button,
 * and M2 must not grow its own printer client to get one. This file is the
 * typed local port M2 codes against; it forwards to
 * `POST /api/print/label` (permission `warehouse.print`, answers 202).
 *
 * The one shape change the swap forced: M9 takes the label **payload**, not an
 * entity id. That is deliberate on its side — a caller can never supply ZPL, a
 * host or a port (docs/recon/printing-scanners.md A6 #8), and the template that
 * renders `pallet_label` / `bin_label` lives on the server. Both call sites
 * already have the row on screen, so they pass what the label prints.
 *
 * Contract (ARCHITECTURE §8.4, MODULES.md M9):
 *   - label kinds: `pallet_label`, `bin_label`
 *   - permission: `warehouse.print` (NOT admin-only)
 *   - no printer is named ⇒ the server resolves the station default and
 *     answers `E_PRINTER_UNAVAILABLE` when nothing is registered. The web
 *     renders that as `warehouseCore:pallets.printUnavailable`.
 */

export type PrintLabelKind = 'pallet_label' | 'bin_label';

export interface PalletLabelFields {
  lpn: string;
  articleNo?: string | null | undefined;
  articleName?: string | null | undefined;
  qty?: string | null | undefined;
  unit?: string | null | undefined;
  lotNo?: string | null | undefined;
  bestBefore?: string | null | undefined;
  binCode?: string | null | undefined;
}

export interface BinLabelFields {
  binCode: string;
  areaName?: string | null | undefined;
  shelfCode?: string | null | undefined;
  isPickLocation?: boolean | undefined;
}

export type PrintLabelRequest =
  | { kind: 'pallet_label'; payload: PalletLabelFields; copies?: number; printerId?: string }
  | { kind: 'bin_label'; payload: BinLabelFields; copies?: number; printerId?: string };

export interface PrintLabelResult {
  jobId: string;
  queued: boolean;
}

export interface PrintPort {
  /** True since M9 devices-printing landed — kept so call sites stay unchanged. */
  readonly available: boolean;
  print(request: PrintLabelRequest): Promise<PrintLabelResult>;
}

class M9PrintPort implements PrintPort {
  readonly available = true;

  async print(request: PrintLabelRequest): Promise<PrintLabelResult> {
    const result = await api.post<PrintJobResult>('/api/print/label', {
      kind: request.kind,
      payload: dropEmpty({ ...request.payload }),
      ...(request.copies ? { copies: request.copies } : {}),
      ...(request.printerId ? { printerId: request.printerId } : {}),
    });
    return { jobId: result.jobId, queued: result.status !== 'failed' };
  }
}

/** The label schemas take no nulls; send only the fields we actually know. */
function dropEmpty(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
}

export const printPort: PrintPort = new M9PrintPort();
